import axios from 'axios';

/**
 * BackendWarmupService - Wakes up the backend server after user login
 *
 * Problem: Cloud Run/Render instances go to sleep after inactivity.
 * First request after sleep has high latency (cold start).
 *
 * Solution: Ping the backend immediately after login to wake it up,
 * so when user performs their first action, server is already warm.
 */

const BACKEND_URL =
  import.meta.env.VITE_API_URL ||
  'https://entropia-1058627764168.europe-west1.run.app';
const WARMUP_TIMEOUT = 20000; // 20 seconds for cold start

/**
 * Ping the backend to wake it up
 * Uses /health endpoint which returns {"status":"ok"}
 */
export const warmupBackend = async (): Promise<boolean> => {
  const endpoints = [
    '/health',     // Health endpoint - returns {"status":"ok"}
    '/',           // Root endpoint as fallback
  ];

  for (const endpoint of endpoints) {
    try {
      await axios.get(`${BACKEND_URL}${endpoint}`, {
        timeout: WARMUP_TIMEOUT,
        validateStatus: () => true, // Accept any status code
      });
      console.log('[Warmup] Backend is awake');
      return true;
    } catch {
      // Try next endpoint
      continue;
    }
  }

  console.warn('[Warmup] Could not reach backend');
  return false;
};

/**
 * Silent warmup - doesn't log anything
 * Use this for background warmup
 */
export const silentWarmup = async (): Promise<void> => {
  try {
    await axios.get(`${BACKEND_URL}/health`, {
      timeout: WARMUP_TIMEOUT,
    });
  } catch {
    // Silently ignore
  }
};
